import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { IcpSegmentInput } from './types';

export const icpSegmentKeys = {
  all: ['icp-segments'] as const,
  list: (params: api.IcpSegmentListParams) => [...icpSegmentKeys.all, 'list', params] as const,
};

/** The org's segments — a short list, fetched whole for pickers and filters. */
export function useIcpSegments(params: api.IcpSegmentListParams = {}, enabled = true) {
  return useQuery({
    queryKey: icpSegmentKeys.list(params),
    queryFn: () => api.listIcpSegments({ page_size: 200, ...params }),
    enabled,
    staleTime: 60_000,
  });
}

function useSegmentMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  success: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: icpSegmentKeys.all });
      toast.success(success);
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message); // field errors go to the form
    },
  });
}

export function useCreateIcpSegment() {
  return useSegmentMutation((input: IcpSegmentInput) => api.createIcpSegment(input), 'Segment created');
}

export function useUpdateIcpSegment() {
  return useSegmentMutation(
    ({ id, input }: { id: number; input: IcpSegmentInput }) => api.updateIcpSegment(id, input),
    'Segment updated',
  );
}

export function useArchiveIcpSegment() {
  return useSegmentMutation((id: number) => api.archiveIcpSegment(id), 'Segment archived');
}

export function useRestoreIcpSegment() {
  return useSegmentMutation((id: number) => api.restoreIcpSegment(id), 'Segment restored');
}
